import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, IconButton, Tooltip, Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedIcon from '@mui/icons-material/Verified';
import { pactFootprintService, leiFromCompanyIds } from '../services/pactFootprintService';

/**
 * PACT Footprints — read-only view of ELM product footprints in the WBCSD PACT
 * ProductFootprint shape, surfacing the data owner's LEI (urn:lei) injected into
 * companyIds (Phase 1, PACT × GLEIF/vLEI).
 */
function PACTFootprints() {
  const navigate = useNavigate();
  const [footprints, setFootprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await pactFootprintService.getFootprints({ limit: 100 });
      setFootprints(result.data || []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const renderCompanyIds = (companyIds) => {
    if (!Array.isArray(companyIds) || companyIds.length === 0) {
      return <Typography variant="caption" color="text.secondary">—</Typography>;
    }
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {companyIds.map((id) => {
          const isLei = String(id).toLowerCase().startsWith('urn:lei:');
          return isLei ? (
            <Chip key={id} label={id} size="small" color="success" variant="outlined" icon={<VerifiedIcon />} />
          ) : (
            <Chip key={id} label={id} size="small" variant="outlined" />
          );
        })}
      </Stack>
    );
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" gutterBottom>PACT Footprints</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        Product footprints exported in the WBCSD PACT ProductFootprint shape. The data owner's
        ISO 17442 LEI is surfaced in <code>companyIds</code> as <code>urn:lei:&lt;LEI&gt;</code>.
        Click a row for the full PACT ProductFootprint document.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>LEI</TableCell>
              <TableCell>Company IDs</TableCell>
              <TableCell>PCF (excl. biogenic)</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {footprints.map((fp) => {
              const lei = leiFromCompanyIds(fp.companyIds);
              return (
                <TableRow
                  key={fp.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/pact-footprints/${fp.id}`)}
                >
                  <TableCell>{fp.productNameCompany || fp.id}</TableCell>
                  <TableCell>{fp.companyName}</TableCell>
                  <TableCell>
                    {lei
                      ? <Chip label={lei} size="small" color="success" icon={<VerifiedIcon />} />
                      : <Chip label="No LEI" size="small" variant="outlined" />}
                  </TableCell>
                  <TableCell>{renderCompanyIds(fp.companyIds)}</TableCell>
                  <TableCell>
                    {fp.pcf?.pcfExcludingBiogenicUptake ?? '—'}
                    {fp.pcf?.declaredUnitOfMeasurement ? ` kgCO2e / ${fp.pcf.declaredUnitAmount ?? 1} ${fp.pcf.declaredUnitOfMeasurement}` : ''}
                  </TableCell>
                  <TableCell><Chip label={fp.status} size="small" variant="outlined" /></TableCell>
                </TableRow>
              );
            })}
            {footprints.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No product footprints found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default PACTFootprints;
